import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Shield, Trash2, AlertTriangle, ArrowLeft } from "lucide-react";

/**
 * /profile/security — Panel de "Sesiones activas / IPs conocidas".
 *
 * Muestra las últimas IPs hash conocidas del usuario. Permite revocar
 * individualmente o todas (panic). Revocar una IP no invalida sesiones
 * JWT activas, pero hace que el próximo login desde esa IP dispare la
 * notificación de "nuevo inicio de sesión".
 */
export default function ProfileSecurityPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, last_login_at: null });
  const [loading, setLoading] = useState(true);
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmOne, setConfirmOne] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/sessions");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando sesiones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revokeOne = async () => {
    if (!confirmOne) return;
    setSubmitting(true);
    try {
      await api.delete(`/auth/sessions/${encodeURIComponent(confirmOne.ip_hash_full)}`);
      toast.success(`IP ${confirmOne.ip_hash_short} revocada`);
      setConfirmOne(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error revocando");
    } finally {
      setSubmitting(false);
    }
  };

  const revokeAll = async () => {
    setSubmitting(true);
    try {
      await api.delete("/auth/sessions");
      toast.success("Todas las IPs revocadas");
      setConfirmAll(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error revocando todas");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-3" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-1" /> Volver
      </Button>
      <h1 className="font-serif text-3xl text-gray-900 mb-1 flex items-center gap-2">
        <Shield className="w-7 h-7 text-brand-burgundy" /> Seguridad de la cuenta
      </h1>
      <p className="text-sm text-gray-600 mb-6">
        Aquí ves las ubicaciones (hashes de IP) desde las que has iniciado sesión recientemente.
        Si reconoces alguna que no es tuya, revócala — la próxima vez que se intente entrar desde ahí
        recibirás una notificación por email.
      </p>

      {data.last_login_at && (
        <div className="mb-4 text-xs text-gray-500 border-l-2 border-brand-burgundy/30 pl-3">
          Último inicio de sesión registrado: <strong>{formatDate(data.last_login_at)}</strong>
        </div>
      )}

      <div className="border border-gray-200 rounded-sm">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm font-semibold text-gray-900">
            IPs conocidas ({data.total})
          </div>
          {data.total > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={() => setConfirmAll(true)}
              data-testid="revoke-all-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Revocar todas
            </Button>
          )}
        </div>
        {loading && <div className="p-6 text-center text-gray-500">Cargando…</div>}
        {!loading && data.total === 0 && (
          <div className="p-8 text-center text-gray-500 text-sm">
            Sin IPs registradas todavía. La primera vez que inicies sesión, tu IP quedará marcada
            como conocida (sin notificación). A partir de la segunda IP distinta, recibirás un email.
          </div>
        )}
        {!loading && data.items.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {data.items.map((it, idx) => (
              <li key={it.ip_hash_full} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50" data-testid={`session-row-${idx}`}>
                <div>
                  <code className="text-xs font-mono text-gray-700">{it.ip_hash_short}…</code>
                  <div className="text-[11px] text-gray-500">
                    {idx === data.items.length - 1 ? "Más reciente" : `#${idx + 1}`} · Hash truncado (sin IP real)
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-rose-700 hover:bg-rose-50"
                  onClick={() => setConfirmOne(it)}
                  data-testid={`revoke-btn-${idx}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Revocar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6 text-[12px] text-gray-500 leading-relaxed">
        <strong>Privacidad:</strong> nunca almacenamos tu IP real. Solo guardamos un hash SHA-256
        truncado para detectar logins desde ubicaciones nuevas. Revocar una IP no cierra sesiones
        activas (los tokens JWT siguen siendo válidos hasta su expiración). Para cerrar todas las
        sesiones, cambia tu contraseña — eso invalida los tokens actuales.
      </div>

      {/* Modal: confirmar revocar UNA */}
      <Dialog open={!!confirmOne} onOpenChange={(o) => !o && !submitting && setConfirmOne(null)}>
        <DialogContent className="max-w-md" data-testid="confirm-one-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> ¿Revocar esta IP?
            </DialogTitle>
            <DialogDescription>
              <code className="bg-gray-100 px-1">{confirmOne?.ip_hash_short}…</code>
              <div className="mt-2 text-gray-700">
                La próxima vez que se intente iniciar sesión desde esta IP recibirás un email
                de "Nuevo inicio de sesión detectado".
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOne(null)} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              onClick={revokeOne}
              disabled={submitting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="confirm-revoke-one"
            >
              {submitting ? "Revocando…" : "Revocar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: confirmar revocar TODAS */}
      <Dialog open={confirmAll} onOpenChange={(o) => !o && !submitting && setConfirmAll(false)}>
        <DialogContent className="max-w-md" data-testid="confirm-all-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> ¿Revocar TODAS las IPs?
            </DialogTitle>
            <DialogDescription>
              Vas a borrar las {data.total} IPs conocidas de tu cuenta. La próxima vez que inicies
              sesión, tu IP volverá a ser marcada como nueva (sin email, es la primera) y a partir
              de la segunda recibirás notificaciones.
              <div className="mt-2 font-medium text-rose-800">
                Esto NO cierra sesiones activas — para eso, cambia tu contraseña.
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAll(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              onClick={revokeAll}
              disabled={submitting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="confirm-revoke-all"
            >
              {submitting ? "Revocando…" : "Revocar todas"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
